/**
 * Finance Detail Page
 * Display detailed transaction information
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MainLayout } from '../../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState } from '../../../components/common';
import { Card, CardContent } from '../../../components/ui';
import { FinanceService } from '../../../services/finance/FinanceService';
import type { FinancialTransactionDetail } from '../../../types/finance';

const FinanceDetailPage: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const [transaction, setTransaction] = useState<FinancialTransactionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTransactionDetail = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const detail = await FinanceService.getTransactionDetail(id);
        setTransaction(detail);
      } catch (error) {
        console.error('Failed to fetch transaction detail:', error);
        setTransaction(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactionDetail();
  }, [id]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(amount));
  };

  const formatFinanceDate = (date: Date | string | null | undefined): string => {
    if (!date) return 'N/A';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(dateObj);
  };

  const getStatusColor = (status: string, theme: any): string => {
    switch (status) {
      case 'Settled':
        return theme.palette.success.main;
      case 'SettlementSubmitted':
        return theme.palette.info.main;
      case 'Authorized':
      case 'Expired':
        return theme.palette.warning.main;
      case 'Failed':
      case 'Voided':
        return theme.palette.error.main;
      case 'Refunded':
        return theme.palette.grey[500];
      default:
        return theme.palette.text.secondary;
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!transaction) {
    return (
      <MainLayout>
        <EmptyState
          title="Transaction Not Found"
          message="The transaction you're looking for doesn't exist or has been removed."
        />
      </MainLayout>
    );
  }

  const transactionInformationItems: Array<{ label: string; value: React.ReactNode; color?: string }> = [
    { label: 'Merchant', value: transaction.merchant || 'N/A' },
    { label: 'Merchant Account', value: transaction.merchantAccount || 'N/A' },
    { label: 'Transaction Type', value: transaction.operationType === 'Refund' ? 'refund' : 'sale' },
    { label: 'Provider', value: transaction.provider || 'N/A' },
    { label: 'Amount', value: `${formatCurrency(transaction.amount)} USD` },
    { label: 'Settlement Amount', value: `${formatCurrency(transaction.settlementAmount)} USD` },
    { label: 'Status', value: transaction.status, color: getStatusColor(transaction.status, theme) },
    { label: 'Transaction Date', value: formatFinanceDate(transaction.date) },
    { label: 'Provider Reference', value: transaction.providerReference || transaction.transactionId || 'N/A' },
    { label: 'Payment Method Reference', value: transaction.paymentMethodReference || 'N/A' },
    ...(transaction.authorizationExpiresAt
      ? [{ label: 'Authorization Expires At', value: formatFinanceDate(transaction.authorizationExpiresAt) }]
      : []),
    ...(transaction.settledAt ? [{ label: 'Settled At', value: formatFinanceDate(transaction.settledAt) }] : []),
    ...(transaction.voidedAt ? [{ label: 'Voided At', value: formatFinanceDate(transaction.voidedAt) }] : []),
    ...(transaction.failureReason ? [{ label: 'Failure Reason', value: transaction.failureReason }] : []),
  ];

  const renderInfoItem = (label: string, value: React.ReactNode, color?: string) => (
    <Box sx={{ marginBottom: theme.spacing(2) }}>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: theme.typography.caption.fontSize,
          marginBottom: theme.spacing(0.5),
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: color ?? theme.palette.text.primary,
          fontWeight: 500,
          fontSize: theme.typography.body2.fontSize,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <MainLayout>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing(3),
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          Mission Title: {transaction.missionTitle || 'N/A'}
        </Typography>
      </Box>

      <Card sx={{ marginBottom: theme.spacing(3) }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              marginBottom: theme.spacing(3),
              color: theme.palette.text.primary,
              fontSize: '1rem',
            }}
          >
            Transaction Information
          </Typography>
          <Grid container spacing={3}>
            {transactionInformationItems.map((item) => (
              <Grid key={item.label} item xs={12} sm={6}>
                {renderInfoItem(item.label, item.value, item.color)}
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default FinanceDetailPage;
